/**
 * Error Utilities for TENSION App
 * 
 * Provides error logging, reporting, and recovery helpers
 */

export interface ErrorLog {
  id: string;
  timestamp: string;
  message: string;
  stack?: string;
  componentStack?: string;
  userAgent: string;
  url: string;
  userId?: string;
}

/**
 * Log error to localStorage for debugging
 */
export function logError(error: Error, errorInfo?: { componentStack?: string }): ErrorLog {
  const errorLog: ErrorLog = {
    id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    message: error.message,
    stack: error.stack,
    componentStack: errorInfo?.componentStack,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  try {
    // Get existing errors
    const storedErrors = localStorage.getItem('tension_error_logs');
    const errors: ErrorLog[] = storedErrors ? JSON.parse(storedErrors) : [];

    // Add new error (keep last 50)
    errors.push(errorLog);
    if (errors.length > 50) {
      errors.shift();
    }

    localStorage.setItem('tension_error_logs', JSON.stringify(errors));

    // Push to backend so admin can see in Error Logs / Error Tracking (fire-and-forget)
    import('./db-storage').then(({ reportError }) => {
      reportError({
        id: errorLog.id,
        timestamp: errorLog.timestamp,
        message: errorLog.message,
        stack: errorLog.stack,
        componentStack: errorLog.componentStack,
        userAgent: errorLog.userAgent,
        url: errorLog.url,
        userId: errorLog.userId,
      }).catch(() => {});
    }).catch(() => {});

    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('🔴 Error logged:', errorLog);
    }
  } catch (e) {
    // Fallback if localStorage fails
    console.error('Failed to log error to localStorage:', e);
    console.error('Original error:', errorLog);
  }

  return errorLog;
}

/**
 * Get all error logs
 */
export function getErrorLogs(): ErrorLog[] {
  try {
    const storedErrors = localStorage.getItem('tension_error_logs');
    return storedErrors ? JSON.parse(storedErrors) : [];
  } catch (e) {
    console.error('Failed to retrieve error logs:', e);
    return [];
  }
}

/**
 * Clear all error logs
 */
export function clearErrorLogs(): void {
  try {
    localStorage.removeItem('tension_error_logs');
    console.log('✅ Error logs cleared');
  } catch (e) {
    console.error('Failed to clear error logs:', e);
  }
}

/**
 * Export error logs as JSON file
 */
export function exportErrorLogs(): void {
  try {
    const errors = getErrorLogs();
    const dataStr = JSON.stringify(errors, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `tension-error-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
    console.log('✅ Error logs exported');
  } catch (e) {
    console.error('Failed to export error logs:', e);
  }
}

/**
 * Send error report (placeholder - would integrate with error tracking service)
 */
export async function sendErrorReport(errorLog: ErrorLog): Promise<boolean> {
  try {
    // In production, this would send to Sentry, LogRocket, etc.
    // For now, just log to console
    console.log('📤 Would send error report:', errorLog);
    
    // Placeholder for future integration
    // await fetch('https://api.yourservice.com/errors', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(errorLog),
    // });
    
    return true;
  } catch (e) {
    console.error('Failed to send error report:', e);
    return false;
  }
}

/**
 * Check if error is recoverable
 */
export function isRecoverableError(error: Error): boolean {
  const recoverablePatterns = [
    /loading chunk/i,
    /failed to fetch/i,
    /network error/i,
    /timeout/i,
  ];
  
  return recoverablePatterns.some(pattern => pattern.test(error.message));
}

/**
 * Get user-friendly error message
 */
export function getFriendlyErrorMessage(error: Error): string {
  const message = error.message.toLowerCase();
  
  if (message.includes('network') || message.includes('fetch')) {
    return "We're having trouble connecting. Please check your internet connection and try again.";
  }
  
  if (message.includes('chunk') || message.includes('loading')) {
    return "There was a problem loading this page. Please refresh and try again.";
  }
  
  if (message.includes('storage') || message.includes('quota')) {
    return "Your device storage is full. Please free up some space and try again.";
  }
  
  if (message.includes('permission')) {
    return "We don't have the necessary permissions. Please check your browser settings.";
  }
  
  return "Something went wrong. We've logged the error and will fix it soon.";
}

/**
 * Attempt to recover from error
 */
export async function attemptRecovery(error: Error): Promise<boolean> {
  try {
    // Clear potentially corrupted data
    const corruptedKeys = [
      'tension_temp_',
      'tension_cache_',
    ];
    
    Object.keys(localStorage).forEach(key => {
      if (corruptedKeys.some(prefix => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    });
    
    // Force reload if it's a chunk loading error
    if (error.message.includes('chunk')) {
      window.location.reload();
      return true;
    }
    
    return true;
  } catch (e) {
    console.error('Recovery failed:', e);
    return false;
  }
}
